/**
 * <mb-cart-coupon>: cupom do carrinho (portado de components/coupon.js).
 *
 * A Vnda tinha um endpoint próprio (POST /cupom/ajax) que respondia se o cupom valia. Na Shopify o cupom vai em
 * POST /cart/update.js com { discount: "CODIGO" }, e a resposta diz se ele valeu: cart.discount_codes traz
 * { code, applicable }. Código que não existe, vencido ou fora da regra (coleção, valor mínimo) volta igual,
 * com applicable: false, então a mensagem de erro cobre os três casos.
 *
 * O cupom que não vale não fica no carrinho: o anterior (data-applied-code) volta no lugar, como na Vnda.
 * A seção vem re-renderizada na própria resposta (sections) e o snippet cart-coupon marca .-applied a partir
 * de cart.discount_applications, então a tela nunca mostra "aplicado" sem o desconto existir de fato.
 */

/** Estado visual depois do re-render (o elemento antigo já não existe). */
function applyState(root, code, notFound) {
  const coupon = root && root.querySelector('mb-cart-coupon');
  if (!coupon) return;
  coupon.classList.add('-active');
  const input = coupon.querySelector('.input');
  const error = coupon.querySelector('.result.-error');
  if (notFound) {
    // O cupom anterior continua no carrinho (e no preço), mas o campo agora mostra o código que falhou:
    // "Cupom aplicado!" junto do erro confundia.
    coupon.querySelector('.result.-success')?.classList.remove('-visible');
    if (input) input.value = code;
    if (error) error.classList.add('-visible');
    if (input) input.focus();
  }
}

class MbCartCoupon extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-coupon-form]');
    this.input = this.querySelector('.input');

    this.querySelector('[data-coupon-toggle]')?.addEventListener('click', () => {
      this.classList.toggle('-active');
      if (this.classList.contains('-active') && this.input) this.input.focus();
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const code = (this.input.value || '').trim();
      if (code) this.apply(code);
    });

    this.querySelector('[data-coupon-remove]')?.addEventListener('click', () => this.remove());
  }

  get root() {
    return this.closest('mb-cart-drawer, mb-cart-page');
  }

  setSending(state) {
    this.form?.classList.toggle('-sending', Boolean(state));
  }

  /** Grava o cupom no carrinho e devolve o carrinho com o HTML da seção (null se a requisição falhar). */
  async post(discount, sectionId) {
    try {
      const response = await fetch(`${window.theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount, sections: sectionId }),
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async apply(code) {
    const root = this.root;
    if (!root) return;
    this.setSending(true);
    const previous = this.dataset.appliedCode || '';

    let cart = await this.post(code, root.sectionId);
    const entry = (cart?.discount_codes || []).find((item) => item.code.toUpperCase() === code.toUpperCase());
    const applied = Boolean(entry && entry.applicable);
    if (cart && !applied) cart = await this.post(previous, root.sectionId);

    const html = cart?.sections?.[root.sectionId];
    await root.refresh(typeof html === 'string' ? html : undefined);
    applyState(root, code, !applied);
  }

  async remove() {
    const root = this.root;
    if (!root) return;
    this.setSending(true);
    const cart = await this.post('', root.sectionId);
    const html = cart?.sections?.[root.sectionId];
    await root.refresh(typeof html === 'string' ? html : undefined);
  }
}

if (!customElements.get('mb-cart-coupon')) customElements.define('mb-cart-coupon', MbCartCoupon);
